import { Link, useNavigate } from "react-router-dom"
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react"
import { useCart } from "../../context/cart-context"
import Breadcrumbs from "../../components/goods/bread-crumb";
import QuantitySelector from "../../components/goods/quantity-selector";

export default function CartPage() {
  const { items, totalItems, totalAmount, removeFromCart, updateQuantity } = useCart()
  const navigate = useNavigate()

  // Calculate shipping cost
  const shippingCost = totalAmount > 100 ? 0 : 10

  // Calculate tax (assuming 8%)
  const taxRate = 0.08
  const taxAmount = totalAmount * taxRate

  // Calculate order total
  const orderTotal = totalAmount + shippingCost + taxAmount

  if (totalItems === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={[{ label: "Cart" }]} />

        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <ShoppingBag className="h-8 w-8 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
          <p className="text-gray-500 mb-6">Looks like you haven't added any products to your cart yet.</p>
          <Link
            to="/goods"
            className="inline-flex items-center justify-center bg-[#ff9906] hover:bg-[#e68a00] text-white py-2 px-4 rounded-md font-medium transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumbs items={[{ label: "Cart" }]} />

      <h1 className="text-2xl font-bold mb-6">
        Shopping Cart ({totalItems} {totalItems === 1 ? "item" : "items"})
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              {items.map((item) => (
                <div key={item.id} className="p-4 sm:p-6 flex flex-col sm:flex-row gap-4">
                  {/* Product Image */}
                  <div className="w-full sm:w-24 h-24 flex-shrink-0">
                    <img
                      src={item.image || "/placeholder.svg"}
                      alt={item.name}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Product Details */}
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <Link
                        to={`/goods/product/${item.id}`}
                        className="text-lg font-medium text-gray-800 hover:text-[#ff9906]"
                      >
                        {item.name}
                      </Link>
                      <span className="font-bold text-[#ff9906]">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>

                    <p className="text-sm text-gray-500 mt-1">${item.price.toFixed(2)} each</p>

                    <div className="flex items-center justify-between mt-4">
                      <QuantitySelector
                        quantity={item.quantity}
                        onIncrease={() => updateQuantity(item.id, item.quantity + 1)}
                        onDecrease={() => updateQuantity(item.id, item.quantity - 1)}
                        onQuantityChange={(value) => updateQuantity(item.id, value)}
                      />

                      <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-red-500">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-between items-center">
            <Link to="/goods" className="text-[#ff9906] hover:underline flex items-center gap-1">
              <ArrowRight className="h-4 w-4 rotate-180" />
              Continue Shopping
            </Link>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold mb-4">Order Summary</h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">${totalAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium">{shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Tax (8%)</span>
                <span className="font-medium">${taxAmount.toFixed(2)}</span>
              </div>

              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-[#ff9906]">${orderTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate("/goods/checkout")}
              className="w-full bg-[#ff9906] hover:bg-[#e68a00] text-white py-3 rounded-md font-medium mt-6 transition-colors"
            >
              Proceed to Checkout
            </button>

            <div className="mt-6 text-xs text-gray-500">
              <p>We accept:</p>
              <div className="flex gap-2 mt-2">
                <div className="bg-gray-100 rounded p-1 w-10 h-6"></div>
                <div className="bg-gray-100 rounded p-1 w-10 h-6"></div>
                <div className="bg-gray-100 rounded p-1 w-10 h-6"></div>
                <div className="bg-gray-100 rounded p-1 w-10 h-6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
